// /analytika/produkty — detail jednoho produktu: cesta k nákupu, kde lidé
// odcházejí a na co klikají (22. 9. 2026). Podoba z mockupu 22. 9. 2026.
import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Target, RefreshCw, Route, ArrowDownToLine, MousePointerClick, AlertTriangle, Info, CalendarRange, Smartphone, Megaphone, Lightbulb } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, Dlazdice, ErrorNote, SkeletonCards, RozbalovaciVyber, EmptyState } from "../../components/ui.jsx";
import {
  KartaPrehledu, HlavickaDlazdice, RadkyPrehledu, RadekPrehledu, Pilulka, HlaskaDlazdice,
} from "../../components/PrehledDlazdice.jsx";
import { useObdobi, VyberObdobi, useAnalytika, cislo, kc, tvar, POPISY_SEKCI } from "./spolecne.jsx";

const PRODUKTY = [
  { key: "bootcampy-kbely", label: "Bootcampy Kbely" },
  { key: "bootcampy-letna", label: "Bootcampy Letná" },
  { key: "dotaznik-reklama", label: "Dotazník z reklam" },
  { key: "trenuj", label: "Trénuj se mnou" },
  { key: "klub", label: "Klub FitWay40" },
  { key: "jsz", label: "Jídelníček spokojené ženy" },
  { key: "jidelnicek", label: "Jídelníček na míru" },
  { key: "kviz", label: "Kvíz" },
];
// Stránka, u které se počítají sekce a odchody.
const HLAVNI = {
  "bootcampy-kbely": "/bootcampy/", "bootcampy-letna": "/bootcampy/", trenuj: "/trenuj-se-mnou/",
  "dotaznik-reklama": "/chci-zkusebni-trenink/",
  klub: "/klub/", jsz: "/jsz/", jidelnicek: "/jidelnicek-na-miru/", kviz: "/kviz/",
};

const KLIC_PRODUKT = "fw40.analytika.produkt";
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

export default function AnalytikaProdukty() {
  const [params, setParams] = useSearchParams();
  // Vybraný produkt se pamatuje v prohlížeči, ať se po návratu na stránku
  // neukáže zase první v pořadí. Odkaz s ?produkt= (z Přehledu) má přednost.
  const zAdresy = params.get("produkt");
  const ulozeny = (() => { try { return localStorage.getItem(KLIC_PRODUKT); } catch { return null; } })();
  const klic = PRODUKTY.some((p) => p.key === zAdresy) ? zAdresy
    : PRODUKTY.some((p) => p.key === ulozeny) ? ulozeny : "bootcampy-kbely";
  useEffect(() => {
    try { localStorage.setItem(KLIC_PRODUKT, klic); } catch { /* nevadí */ }
  }, [klic]);
  const vyberProdukt = (k) => {
    try { localStorage.setItem(KLIC_PRODUKT, k); } catch { /* nevadí */ }
    setParams({ produkt: k });
  };
  const obdobi = useObdobi();
  const hlavni = HLAVNI[klic];
  const { data, loading, error, refetch, refreshing } = useAnalytika(obdobi.rozsah, [hlavni]);
  const pr = (data?.produkty || []).find((p) => p.klic === klic);
  const stranka = data?.stranky?.find((s) => s.path === hlavni);
  const sekce = data?.sekce?.[hlavni];

  const kroky = pr ? [
    { popisek: "Prodejní stránka", hodnota: pr.stranka, doplnek: pr.lokalita ? "obě lokality" : null },
    pr.lokalita ? { popisek: "Vybrala tuhle lokalitu", hodnota: pr.strankaLokalita } : null,
    pr.klik != null ? { popisek: "Klik k nákupu", hodnota: pr.klik } : null,
    pr.pokladna != null ? { popisek: "Pokladna", hodnota: pr.pokladna } : null,
    // Kroky pokladny (16. 10. 2026, zatím jen Klub, schválený mockup).
    pr.zacala != null ? { popisek: "Začala objednávku", hodnota: pr.zacala, pokladni: true } : null,
    pr.objednat != null ? { popisek: "Klikla na objednat", hodnota: pr.objednat, pokladni: true } : null,
    { popisek: pr.typVysledku === "lead" ? "Odeslaný formulář" : "Zaplatila", hodnota: pr.vysledky, konec: true },
  ].filter(Boolean) : [];

  // Největší odpad: krok s nejvyšší ztrátou proti předchozímu. Jen u produktu
  // s měřenou pokladnou a od pokladny dál (klik a pokladna nejsou v řadě,
  // na pokladnu se chodí i rovnou z reklamy). Aspoň 5 lidí, ať to není šum.
  const odpad = (() => {
    if (pr?.zacala == null) return null;
    const i0 = kroky.findIndex((k) => k.popisek === "Pokladna");
    let nej = null;
    for (let i = Math.max(1, i0 + 1); i < kroky.length; i++) {
      const a = kroky[i - 1].hodnota, b = kroky[i].hodnota;
      if (a < 5 || b >= a) continue;
      const ztrata = Math.round(((a - b) / a) * 100);
      if (!nej || ztrata > nej.ztrata) nej = { ztrata, z: kroky[i - 1], na: kroky[i] };
    }
    return nej;
  })();

  return (
    <Stranka>
      <GradientHeader
        icon={Target}
        title="Produkty a stránky"
        napoveda="Cesta k nákupu po jednotlivých produktech. Bootcampy mají jednu prodejní stránku pro obě lokality, lokalita se pozná z výběru na stránce, z pokladny nebo z odkazu s ?kde=. Sekce a odchody se měří od 22. 9. 2026 i s lokalitou."
        loading={refreshing}
        akce={[{ klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: refreshing }]}
      />
      <div className="grid gap-3 lg:grid-cols-2">
        <RozbalovaciVyber
          stitek="Produkt"
          aria-label="Produkt"
          value={klic}
          onChange={vyberProdukt}
          items={PRODUKTY}
        />
        <VyberObdobi obdobi={obdobi} />
      </div>

      {error && <ErrorNote onRetry={refetch}>Data se nepodařilo načíst: {error}</ErrorNote>}
      {loading && !data && <SkeletonCards count={4} />}

      {pr && (
        <>
          <div className="grid grid-cols-2 gap-2 sm:gap-2.5 lg:grid-cols-4">
            <Dlazdice popisek="Návštěvnice" hodnota={cislo(pr.stranka)} ton="znacka" doplnky={pr.lokalita && <Pilulka>obě lokality</Pilulka>} />
            <Dlazdice popisek="Dočtou stránku" hodnota={stranka?.scroll != null ? `${stranka.scroll} %` : "—"} ton="neutralni" doplnky={<Pilulka>průměr</Pilulka>} />
            <Dlazdice popisek="Čas na stránce" hodnota={stranka?.sekund != null ? `${stranka.sekund} s` : "—"} ton="neutralni" />
            <Dlazdice
              popisek={pr.typVysledku === "lead" ? "Kontakty" : "Nákupy"}
              hodnota={cislo(pr.vysledky + (pr.kontaktyMeta || 0))}
              ton={pr.vysledky || pr.kontaktyMeta ? "ok" : pr.reklama ? "problem" : "neutralni"}
              doplnky={pr.trzbaZnama ? <Pilulka>{kc(pr.trzba)}</Pilulka>
                : pr.kontaktyMeta ? <Pilulka>{cislo(pr.vysledky)} z webu, {cislo(pr.kontaktyMeta)} z Mety</Pilulka>
                : pr.reklama ? <Pilulka>reklama {kc(pr.reklama)}</Pilulka> : null}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <KartaPrehledu>
              <HlavickaDlazdice ikona={Route} nazev="Cesta k nákupu" podtitulek="Kolik lidí došlo do každého kroku" />
              <RadkyPrehledu>
                {kroky.map((k, i) => (
                  <RadekPrehledu
                    key={k.popisek}
                    popisek={k.popisek}
                    hodnota={cislo(k.hodnota)}
                    doplnek={i > 0 ? `${pct(k.hodnota, pr.stranka)} %` : k.doplnek}
                    pruh={pct(k.hodnota, pr.stranka)}
                    tonPruhu={k.konec ? "bg-emerald-500" : "bg-brand"}
                  />
                ))}
              </RadkyPrehledu>
              {odpad && (
                <HlaskaDlazdice ton="zluta" ikona={Lightbulb} nadpis="Největší odpad"
                  text={`Mezi kroky ${odpad.z.popisek.toLowerCase()} a ${odpad.na.popisek.toLowerCase()} odejde ${odpad.ztrata} % žen (${cislo(odpad.z.hodnota)} → ${cislo(odpad.na.hodnota)}). Tady se vyplatí testovat jako první.`} />
              )}
              {pr.reklama > 0 && pr.vysledky === 0 && !pr.kontaktyMeta && (
                <HlaskaDlazdice ikona={AlertTriangle} nadpis="Reklama bez výsledku" text={`Za ${kc(pr.reklama)} žádný ${pr.typVysledku === "lead" ? "kontakt" : "nákup"}.`} />
              )}
            </KartaPrehledu>

            <KartaPrehledu>
              <HlavickaDlazdice ikona={ArrowDownToLine} nazev="Kde na stránce odcházejí" podtitulek={hlavni} />
              {sekce?.sekce?.length ? (
                <RadkyPrehledu>
                  {sekce.sekce.map((x) => (
                    <RadekPrehledu
                      key={x.sekce}
                      popisek={POPISY_SEKCI[x.sekce] || x.sekce}
                      hodnota={`${x.odeslaPct} %`}
                      doplnek={`viděla ${x.videlaPct} %`}
                      pruh={x.odeslaPct}
                      tonPruhu={x.odeslaPct >= 40 ? "bg-rose-500" : "bg-brand"}
                      tonHodnoty={x.odeslaPct >= 40 ? "text-rose-700" : "text-slate-900"}
                    />
                  ))}
                </RadkyPrehledu>
              ) : (
                <EmptyState icon={Info} title="Stránka nemá měřené sekce" hint="Sekce se měří jen tam, kde je na webu označíme. Dá se doplnit." />
              )}
            </KartaPrehledu>
          </div>

          {pr.varianty && (
            <div className="grid gap-4 lg:grid-cols-3">
              <KartaPrehledu>
                <HlavickaDlazdice ikona={CalendarRange} nazev="Varianta" podtitulek="Měsíčně, nebo na 3 měsíce" napoveda="Měří se od 16. 10. 2026. Kdo během objednávky variantu přepne, počítá se u obou." />
                <RadkyPrehledu>
                  {pr.varianty.map((v) => (
                    <RadekPrehledu key={v.nazev} popisek={v.nazev} hodnota={`${cislo(v.nakupy)} ${tvar(v.nakupy, "nákup", "nákupy", "nákupů")}`}
                      pilulky={[`začalo ${cislo(v.zacala)}`, `klikla na objednat ${cislo(v.objednat)}`, v.zacala ? `dokončí ${pct(v.nakupy, v.zacala)} %` : null].filter(Boolean).map((text) => ({ text }))} />
                  ))}
                </RadkyPrehledu>
              </KartaPrehledu>
              <KartaPrehledu>
                <HlavickaDlazdice ikona={Smartphone} nazev="Podle zařízení" podtitulek="Kolik návštěvnic koupí" />
                {pr.zarizeni?.length ? (
                  <RadkyPrehledu>
                    {pr.zarizeni.map((z) => (
                      <RadekPrehledu key={z.nazev} popisek={z.nazev} hodnota={`${String(z.konverze).replace(".", ",")} %`} doplnek={`${cislo(z.nakupy)} z ${cislo(z.navstevnice)}`}
                        pruh={Math.min(100, Math.round(z.konverze * 5))} />
                    ))}
                  </RadkyPrehledu>
                ) : <EmptyState icon={Smartphone} title="Zatím bez dat" hint="Za vybrané období tu nikdo nebyl." />}
              </KartaPrehledu>
              <KartaPrehledu>
                <HlavickaDlazdice ikona={Megaphone} nazev="Podle zdroje" podtitulek="Odkud přišly a kolik koupilo" />
                {pr.zdrojeProduktu?.length ? (
                  <RadkyPrehledu>
                    {pr.zdrojeProduktu.map((z) => (
                      <RadekPrehledu key={z.klic} popisek={z.nazev} hodnota={`${cislo(z.nakupy)} ${tvar(z.nakupy, "nákup", "nákupy", "nákupů")}`}
                        pilulky={[`${cislo(z.navstevnice)} ${tvar(z.navstevnice, "návštěvnice", "návštěvnice", "návštěvnic")}`, `pokladna ${cislo(z.pokladna)}`, z.cena ? `${kc(z.cena)} za nákup` : null].filter(Boolean).map((text) => ({ text }))} />
                    ))}
                  </RadkyPrehledu>
                ) : <EmptyState icon={Megaphone} title="Zatím bez dat" hint="Za vybrané období tu nikdo nebyl." />}
              </KartaPrehledu>
            </div>
          )}

          <KartaPrehledu>
            <HlavickaDlazdice ikona={MousePointerClick} nazev="Na co klikají" odznak={sekce?.akce?.length ? `${sekce.akce.length} tlačítek` : null} />
            {sekce?.akce?.length ? (
              <RadkyPrehledu>
                {sekce.akce.map((a) => (
                  <RadekPrehledu key={a.tlacitko} popisek={a.tlacitko} hodnota={cislo(a.lidi)} pruh={pct(a.lidi, sekce.akce[0].lidi)} />
                ))}
              </RadkyPrehledu>
            ) : (
              <EmptyState icon={MousePointerClick} title="Zatím žádná kliknutí" hint="Za vybrané období nikdo na stránce na nic neklikl." />
            )}
          </KartaPrehledu>

        </>
      )}
    </Stranka>
  );
}
